import { useRef, useState } from "react";
import { z } from "zod";
import { ArrowRight, CheckCircle2, ShieldCheck, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA, trackConversion } from "@/lib/tracking";

/**
 * FSAP Consultation Intake Form.
 *
 * Captures family/student context and Foundation/Priority/Elite tier
 * interest, persists to `inquiry_submissions` (lane="advocacy"), then
 * redirects to STRATEGY_SESSION_URL so the caller can immediately book
 * the paid discovery consultation.
 *
 * STRATEGY_SESSION_URL is read from `VITE_STRATEGY_SESSION_URL`. When
 * unset, the form still saves the inquiry and shows the in-page success
 * state (no redirect). This keeps the form working in preview/staging
 * without leaking a placeholder URL — the placeholder guard in
 * src/test/production-url-placeholders.test.ts fails the build if a
 * fake value is committed.
 */

const STRATEGY_SESSION_URL = import.meta.env.VITE_STRATEGY_SESSION_URL as
  | string
  | undefined;

type Tier = {
  value: "foundation" | "priority" | "elite" | "not_sure";
  name: string;
  tagline: string;
  detail: string;
  featured?: boolean;
};

const TIERS: readonly Tier[] = [
  {
    value: "foundation",
    name: "Foundation",
    tagline: "Monthly guidance",
    detail: "Strategy sessions, document review, email support.",
  },
  {
    value: "priority",
    name: "Priority",
    tagline: "Active representation",
    detail: "Meeting attendance, advocacy planning, faster response.",
    featured: true,
  },
  {
    value: "elite",
    name: "Elite",
    tagline: "High-touch, multi-system",
    detail: "After-hours access, urgent-response, expanded accompaniment.",
  },
  {
    value: "not_sure",
    name: "Not sure — recommend a tier",
    tagline: "Written tier recommendation after discovery",
    detail: "We'll match scope during the discovery consultation.",
  },
] as const;

const URGENCY = [
  { value: "immediate", label: "Immediate (this week)" },
  { value: "weeks", label: "Within a few weeks" },
  { value: "month", label: "Within a month" },
  { value: "planning", label: "Planning ahead" },
];

const fsapSchema = z.object({
  parent_name: z.string().trim().min(2, "Parent/guardian name required").max(100),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().trim().max(32).optional().or(z.literal("")),
  student_first_name: z.string().trim().max(80).optional().or(z.literal("")),
  student_age_grade: z.string().trim().max(80).optional().or(z.literal("")),
  school_or_district: z.string().trim().max(150).optional().or(z.literal("")),
  setting: z.enum(["public_school", "private_school", "college", "home", "multi", "other"]),
  primary_concern: z.string().trim().min(10, "Please share a bit more (10+ chars)").max(2000),
  current_supports: z.string().trim().max(1000).optional().or(z.literal("")),
  tier_interest: z.enum(["foundation", "priority", "elite", "not_sure"]),
  urgency: z.enum(["immediate", "weeks", "month", "planning"]),
  consent: z.literal(true, {
    message: "Please confirm consent to be contacted.",
  }),
});

type FormState = {
  parent_name: string;
  email: string;
  phone: string;
  student_first_name: string;
  student_age_grade: string;
  school_or_district: string;
  setting: "public_school" | "private_school" | "college" | "home" | "multi" | "other" | "";
  primary_concern: string;
  current_supports: string;
  tier_interest: "foundation" | "priority" | "elite" | "not_sure";
  urgency: "immediate" | "weeks" | "month" | "planning";
  consent: boolean;
  website: string; // honeypot
};

const inputCls =
  "bg-navy-elevated/60 border-border text-white placeholder:text-silver/50 focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:border-gold/60 transition-colors";

const labelCls = "text-[11px] uppercase tracking-[0.22em] text-silver mb-2 block";

export const FSAPConsultForm = ({
  defaultTier,
}: {
  defaultTier?: "foundation" | "priority" | "elite" | "not_sure";
}) => {
  const startedAt = useRef<number>(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState<FormState>({
    parent_name: "",
    email: "",
    phone: "",
    student_first_name: "",
    student_age_grade: "",
    school_or_district: "",
    setting: "",
    primary_concern: "",
    current_supports: "",
    tier_interest: defaultTier ?? "not_sure",
    urgency: "weeks",
    consent: false,
    website: "",
  });

  const update =
    <K extends keyof FormState>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((s) => ({ ...s, [k]: e.target.value } as FormState));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.website) return; // honeypot tripped — silent no-op
    if (Date.now() - startedAt.current < 2500) {
      toast.error("Please take a moment to complete the form.");
      return;
    }
    const parsed = fsapSchema.safeParse(form);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Please complete the form");
      return;
    }

    setSubmitting(true);

    const tierLabel =
      TIERS.find((t) => t.value === form.tier_interest)?.name ?? form.tier_interest;
    const urgencyLabel =
      URGENCY.find((u) => u.value === form.urgency)?.label ?? form.urgency;

    // Compose a readable message combining the FSAP-specific structured
    // fields so the existing inquiry_submissions schema captures everything
    // without requiring a migration.
    const composedMessage = [
      `FSAP Consultation Request`,
      ``,
      `Parent/Guardian: ${form.parent_name}`,
      form.phone ? `Phone: ${form.phone}` : null,
      ``,
      `Student: ${form.student_first_name || "—"} (${form.student_age_grade || "age/grade not provided"})`,
      `Setting: ${form.setting}`,
      form.school_or_district ? `School/District: ${form.school_or_district}` : null,
      ``,
      `Tier interest: ${tierLabel}`,
      `Urgency: ${urgencyLabel}`,
      ``,
      `Primary concern:`,
      form.primary_concern,
      form.current_supports
        ? `\nCurrent supports in place:\n${form.current_supports}`
        : null,
    ]
      .filter(Boolean)
      .join("\n");

    const { error } = await supabase.from("inquiry_submissions").insert({
      lane: "advocacy",
      full_name: form.parent_name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim() || null,
      organization: form.school_or_district.trim() || null,
      role: form.student_age_grade.trim() || null,
      event_or_focus: `FSAP tier interest: ${tierLabel}`,
      date_or_timeline: urgencyLabel,
      budget_range: tierLabel,
      message: composedMessage,
      source: "advocacy-fsap-intake",
    });

    setSubmitting(false);

    if (error) {
      console.error("FSAP inquiry insert error:", error);
      toast.error("Something went wrong. Please try again or email us.");
      return;
    }

    trackConversion("fsap_consult_submit", {
      tier: form.tier_interest,
      urgency: form.urgency,
      setting: form.setting,
    });
    trackConversion("inquiry_submit", { lane: "advocacy" });

    setDone(true);

    // Redirect to the paid strategy-session booker only when configured
    // to a real URL. The placeholder guard test blocks fake values.
    if (STRATEGY_SESSION_URL && /^https?:\/\//i.test(STRATEGY_SESSION_URL)) {
      trackCTA("fsap_redirect_to_strategy_session", { tier: form.tier_interest });
      const target = new URL(STRATEGY_SESSION_URL);
      target.searchParams.set("email", form.email.trim().toLowerCase());
      target.searchParams.set("name", form.parent_name.trim());
      target.searchParams.set("tier", form.tier_interest);
      // Small delay so the success state is visible before nav.
      window.setTimeout(() => {
        window.location.href = target.toString();
      }, 900);
    }
  };

  if (done) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="border border-gold/40 bg-gradient-to-br from-gold/[0.08] via-navy to-navy p-8 md:p-10 text-center"
      >
        <CheckCircle2 className="h-12 w-12 text-gold mx-auto" aria-hidden />
        <h3 className="font-display uppercase text-white text-2xl mt-5">
          Consultation request <span className="text-gradient-gold">received.</span>
        </h3>
        <p className="text-silver text-sm md:text-base mt-4 max-w-xl mx-auto leading-relaxed">
          {STRATEGY_SESSION_URL
            ? "Redirecting you to book the paid discovery consultation now…"
            : `Dr. Sully's team responds within one business day at ${form.email}.`}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-border bg-navy/70 backdrop-blur-sm p-6 md:p-10 space-y-8"
      noValidate
    >
      {/* Honeypot */}
      <div className="hidden" aria-hidden>
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
          />
        </label>
      </div>

      {/* Contact */}
      <fieldset className="space-y-5">
        <legend className="font-display uppercase text-white text-sm tracking-[0.22em]">
          Parent / Guardian
        </legend>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="parent_name" className={labelCls}>
              Full name *
            </Label>
            <Input
              id="parent_name"
              required
              value={form.parent_name}
              onChange={update("parent_name")}
              className={inputCls}
              autoComplete="name"
            />
          </div>
          <div>
            <Label htmlFor="email" className={labelCls}>
              Email *
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={update("email")}
              className={inputCls}
              autoComplete="email"
            />
          </div>
          <div>
            <Label htmlFor="phone" className={labelCls}>
              Phone (optional)
            </Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={update("phone")}
              className={inputCls}
              autoComplete="tel"
            />
          </div>
        </div>
      </fieldset>

      {/* Student context */}
      <fieldset className="space-y-5">
        <legend className="font-display uppercase text-white text-sm tracking-[0.22em]">
          Student context
        </legend>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="student_first_name" className={labelCls}>
              Student first name (optional)
            </Label>
            <Input
              id="student_first_name"
              value={form.student_first_name}
              onChange={update("student_first_name")}
              className={inputCls}
            />
          </div>
          <div>
            <Label htmlFor="student_age_grade" className={labelCls}>
              Age / Grade
            </Label>
            <Input
              id="student_age_grade"
              placeholder="e.g. 14 / 9th grade"
              value={form.student_age_grade}
              onChange={update("student_age_grade")}
              className={inputCls}
            />
          </div>
          <div>
            <Label htmlFor="school_or_district" className={labelCls}>
              School or district
            </Label>
            <Input
              id="school_or_district"
              value={form.school_or_district}
              onChange={update("school_or_district")}
              className={inputCls}
            />
          </div>
          <div>
            <Label htmlFor="setting" className={labelCls}>
              Primary setting *
            </Label>
            <select
              id="setting"
              required
              value={form.setting}
              onChange={update("setting")}
              className={`${inputCls} h-10 w-full px-3 rounded-md border`}
            >
              <option value="" disabled>
                Select setting…
              </option>
              <option value="public_school">Public school</option>
              <option value="private_school">Private / faith-based school</option>
              <option value="college">College / university</option>
              <option value="home">Homeschool</option>
              <option value="multi">Multiple settings</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="primary_concern" className={labelCls}>
            What's happening right now? *
          </Label>
          <Textarea
            id="primary_concern"
            required
            rows={5}
            value={form.primary_concern}
            onChange={update("primary_concern")}
            placeholder="Briefly describe the current situation, key stakeholders, timelines, and what's already been tried."
            className={inputCls}
          />
        </div>

        <div>
          <Label htmlFor="current_supports" className={labelCls}>
            Current supports in place (optional)
          </Label>
          <Textarea
            id="current_supports"
            rows={3}
            value={form.current_supports}
            onChange={update("current_supports")}
            placeholder="IEP / 504, clinicians, prior advocacy, legal counsel, etc."
            className={inputCls}
          />
        </div>
      </fieldset>

      {/* Tier interest */}
      <fieldset className="space-y-4">
        <legend className="font-display uppercase text-white text-sm tracking-[0.22em]">
          Tier interest *
        </legend>
        <div className="grid md:grid-cols-2 gap-3">
          {TIERS.map((t) => {
            const selected = form.tier_interest === t.value;
            return (
              <label
                key={t.value}
                className={`relative cursor-pointer border p-4 md:p-5 transition-all ${
                  selected
                    ? "border-gold bg-gold/[0.08] shadow-[0_10px_40px_-20px_hsl(var(--gold)/0.5)]"
                    : "border-border bg-navy-deep/40 hover:border-gold/40"
                } ${t.featured && !selected ? "border-gold/40" : ""}`}
              >
                <input
                  type="radio"
                  name="tier_interest"
                  value={t.value}
                  checked={selected}
                  onChange={() =>
                    setForm((s) => ({ ...s, tier_interest: t.value }))
                  }
                  className="sr-only"
                />
                <div className="flex items-start gap-3">
                  <div
                    className={`h-4 w-4 rounded-full border shrink-0 mt-0.5 flex items-center justify-center ${
                      selected ? "border-gold bg-gold" : "border-silver/40"
                    }`}
                    aria-hidden
                  >
                    {selected && (
                      <Check className="h-3 w-3 text-navy-deep" strokeWidth={3} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display uppercase text-white text-sm tracking-wide">
                        {t.name}
                      </span>
                      {t.featured && (
                        <span className="text-[9px] uppercase tracking-[0.2em] text-orange">
                          Most Requested
                        </span>
                      )}
                    </div>
                    <div className="text-gold text-[11px] uppercase tracking-[0.22em] mt-1">
                      {t.tagline}
                    </div>
                    <p className="text-silver text-xs mt-2 leading-relaxed">
                      {t.detail}
                    </p>
                  </div>
                </div>
              </label>
            );
          })}
        </div>
      </fieldset>

      {/* Urgency */}
      <fieldset>
        <Label htmlFor="urgency" className={labelCls}>
          Urgency *
        </Label>
        <select
          id="urgency"
          required
          value={form.urgency}
          onChange={update("urgency")}
          className={`${inputCls} h-10 w-full md:w-auto px-3 rounded-md border`}
        >
          {URGENCY.map((u) => (
            <option key={u.value} value={u.value}>
              {u.label}
            </option>
          ))}
        </select>
      </fieldset>

      {/* Consent + submit */}
      <div className="space-y-5 border-t border-border pt-6">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={(e) =>
              setForm((s) => ({ ...s, consent: e.target.checked }))
            }
            className="mt-1 h-4 w-4 accent-gold"
            required
          />
          <span className="text-silver text-xs leading-relaxed">
            I understand this is an inquiry — not an engagement — and consent
            to be contacted about a paid discovery consultation. I have
            reviewed the legal &amp; ethical boundaries and understand FSAP is
            professional advocacy, not legal representation.
          </span>
        </label>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-8 py-4 shadow-cta hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting…" : "Submit & book discovery"}
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
          <div className="inline-flex items-center gap-2 text-silver/80 text-[11px] uppercase tracking-[0.2em]">
            <ShieldCheck className="h-3.5 w-3.5 text-gold" aria-hidden />
            One business-day response
          </div>
        </div>

        <Reveal variant="fade" className="text-silver/60 text-[11px] leading-relaxed">
          Submissions are stored securely in Lovable Cloud. After submission
          you're routed to the paid discovery scheduler to confirm fit before
          any engagement is signed.
        </Reveal>
      </div>
    </form>
  );
};
